/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/table.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md (row 10 — same source as brand's List).
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiPlusBold as Plus } from "react-icons/pi";
import { ProductFilters } from "./filters";
import { PagedTable } from "../../../components/table/data-table";
import { ListHeader } from "../../../components/table/list-header";
import { SearchInput } from "../../../components/table/search-input";
import { activeStatusOptions } from "../../../components/table/toolbar";
import { productColumns } from "./columns";
import { CATALOG_MAX_PAGE_SIZE, deactivateProduct, listProducts, reactivateProduct, listBrands, listCategoryTree, loadActiveView, type Product } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { activeViewParams, hrefWith, readActiveView, readPaging } from "../../../lib/paging";
import type { PermissionState, AccountSummary } from "../../../types";
import { EmptyState } from "@steelyard/theme";
import { Alert, Button } from "rizzui";

/**
 * `ProductView` carries only `brandId`/`categoryId` (no denormalized names), so the loader also
 * fetches the brand and category reference lists — small, reference-table-sized, not paginated in
 * practice — and `columns.tsx` joins by id. Both fetched `activeOnly: false` so a product filed
 * under a since-deactivated brand/category still resolves to a name instead of a raw id.
 *
 * `GET /products` filters on `activeOnly`, brand, category and `q` (name or slug, or any of the
 * product's SKUs' name or code — what a storefront page shows) — no inactive-only filter — so
 * `loadActiveView` reads the view and every count under the same brand, category and search.
 */
export async function productsListLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const view = readActiveView(url.searchParams);
  const brandId = url.searchParams.get("brandId") ?? undefined;
  const categoryId = url.searchParams.get("categoryId") ?? undefined;
  const q = url.searchParams.get("q") ?? undefined;

  const [brands, categories, loaded] = await Promise.all([
    listBrands(1, false, CATALOG_MAX_PAGE_SIZE),
    listCategoryTree(false),
    loadActiveView(view, (query) => listProducts({ ...query, brandId, categoryId, q }), readPaging(url.searchParams)),
  ]);
  if (!brands || !categories) throw new Error("The API returned no body for the product list.");
  return { ...loaded, view, brandId, categoryId, q, brands, categories };
}

export default function ProductsList() {
  const { result, view, complete, brandId, categoryId, q, brands, categories, counts } = useLoaderData() as Awaited<ReturnType<typeof productsListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canWrite = can(permissionState, "catalogue.administer");
  const query = (over: Record<string, string | undefined>) => hrefWith("/catalogue/products", searchParams, { ...over, page: undefined });

  const brandNameById = new Map(brands.items.map((b) => [b.id, b.name]));
  const categoryNameById = new Map(categories.items.map((c) => [c.id, c.name]));
  const narrowed = Boolean(brandId || categoryId || q);

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Products"
        count={result.totalCount}
        description="Every marketable item the catalogue groups SKUs under."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "Products" }]}
      />

      {!complete ? (
        <Alert color="warning" variant="flat">
          Inactive shows the inactive products among the first {CATALOG_MAX_PAGE_SIZE} of {counts.all}. The catalogue
          service has no inactive-only filter yet, so any beyond that are not listed here — narrow by brand or category
          to check the rest.
        </Alert>
      ) : null}

      <PagedTable<Product>
        actions={
          canWrite ? (
            <Link to="/catalogue/products/new">
              <Button as="span">
                <Plus aria-hidden className="me-1.5 size-4" />
                New product
              </Button>
            </Link>
          ) : null
        }
        result={result}
        caption="Products, with brand, category and status"
        columns={productColumns(brandNameById, categoryNameById, canWrite ? (product, active) => (active ? reactivateProduct : deactivateProduct)(product.id) : undefined)}
        getRowId={(row) => row.id}
        status={{
          label: "Status",
          options: activeStatusOptions(view, (next) => hrefWith("/catalogue/products", searchParams, activeViewParams(next)), counts),
        }}
        // `q` is a server param, so this searches every product, not only the page on screen — and a
        // SKU's name or code (what the storefront shows) finds its product.
        search={
          <SearchInput
            value={q}
            placeholder={view === "inactive" ? "Search inactive products…" : "Search by product, SKU name or code…"}
            onSearch={(next) => navigate(query({ q: next }))}
          />
        }
        filters={<ProductFilters value={{ brandId, categoryId }} brands={brands.items} categories={categories.items} />}
        clear={{ active: narrowed || view !== "active", href: query({ brandId: undefined, categoryId: undefined, q: undefined, ...activeViewParams("active") }) }}
        empty={
          <EmptyState
            title={narrowed ? "No products matched" : view === "active" ? "No active products" : view === "inactive" ? "No inactive products" : "No products yet"}
            action={
              narrowed ? (
                <Link to="/catalogue/products">
                  <Button as="span" variant="outline" size="sm">
                    Clear the filters
                  </Button>
                </Link>
              ) : canWrite && view === "all" ? (
                <Link to="/catalogue/products/new">
                  <Button as="span" size="sm">
                    <Plus aria-hidden />
                    New product
                  </Button>
                </Link>
              ) : view !== "all" ? (
                <Link to="/catalogue/products?all=1">
                  <Button as="span" variant="outline" size="sm">
                    Show every product
                  </Button>
                </Link>
              ) : null
            }
          >
            {narrowed
              ? q
                ? "No product, or SKU of one, matches that search in this view. Clear it to see every product."
                : "Nothing is filed under that brand or category in this view. Clear the filters to see every product."
              : view === "active"
                ? "No product is active right now — every one has been deactivated."
                : view === "inactive"
                  ? "Every product is active. A deactivated one stays listed here, never removed."
                  : "Create the first one and it becomes selectable when adding a SKU."}
          </EmptyState>
        }
      />
    </div>
  );
}
